import { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast'; 
import Header from './Header';
import Footer from './Footer';
import { LanguageContext } from './LanguageContext';
import { useLocation } from 'react-router-dom';

function CourseDetail() {
  const URL = process.env.REACT_APP_API_URL;
  const { courseId } = useParams(); // Get courseId from the URL
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]); // Store chapters from API
  const [audiences, setAudiences] = useState([]); // Store audiences from API
  const [channels, setChannels] = useState([]); // Store channels from API
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: '',
    company: '',
    address: '',
    taxId: '',
    slip: null,
    channelId: '',
    courseId: courseId,
    isCompany: 0, // Default to personal
    needInvoice: 0, // Default to no invoice
  });
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext); // Access the current language

  // Localized text specific to CourseDetail
  const textContent = {
    th: {
      back: "ย้อนกลับ",
      loading: "กำลังโหลด...",
      notFound: "ไม่พบหลักสูตร",
      objective: "วัตถุประสงค์",
      courseOutline: "หัวข้อการอบรม",
      chapter: "บทที่",
      targetAudience: "กลุ่มเป้าหมาย",
      learningFormat: "รูปแบบการเรียนรู้",
      price: "ราคา",
      priceUnit: "บาท",
      date: "วันที่",
      time: "เวลา",
      place: "สถานที่",
      enroll: "ลงทะเบียน",
      uploadSlip: "อัปโหลดสลิป",
      registrantType: "ประเภทผู้สมัคร",
      personal: "บุคคลธรรมดา",
      company: "นิติบุคคล",
      needInvoice: "ต้องการใบกำกับภาษี",
      yes: "ใช่",
      no: "ไม่ใช่",
      registerSuccess: "คุณได้ลงทะเบียนสำเร็จแล้ว!",
      perceptionChannel: "ช่องทางการรับรู้",
    },
    en: {
      back: "Back",
      loading: "Loading...",
      notFound: "Course not found.",
      objective: "Objective",
      courseOutline: "Course Outline",
      chapter: "Chapter",
      targetAudience: "Target Audience",
      learningFormat: "Learning Format",
      price: "Price",
      priceUnit: "THB",
      date: "Date",
      time: "Time",
      place: "Place",
      enroll: "Enroll",
      uploadSlip: "Upload Slip",
      registrantType: "Registrant Type",
      personal: "Personal",
      company: "Company",
      needInvoice: "Need Invoice",
      yes: "Yes",
      no: "No",
      registerSuccess: "You have registered successfully!",
      perceptionChannel: "Perception Channel",
    },
  };

  const localizedText = textContent[language];
  const location = useLocation();

  // Memoize fetchCourseDetail so it can be used in both useEffect and handleEnroll
  const fetchCourseDetail = useCallback(async () => {
    try {
      const response = await axios.get(`${URL}/courses/${courseId}`);
      setCourse(response.data.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [URL, courseId]);

  useEffect(() => {
    const fetchChapters = async () => {
      try {
        const response = await axios.get(`${URL}/courses/chapters/${courseId}`);        
        setChapters(response.data.data); // Update chapters state with API data
      } catch (error) {        
      }
    };    

    const fetchAudiences = async () => {
      try {
        const response = await axios.get(`${URL}/courses/audiences/${courseId}`);        
        setAudiences(response.data.data); // Update audiences state with API data
      } catch (error) {        
      }
    }; 

    const fetchChannels = async () => {
      try {
        const response = await axios.get(`${URL}/channels`);
        setChannels(response.data.data);
      } catch (error) {        
      }
    };

    fetchCourseDetail();
    fetchChapters();
    fetchAudiences();
    fetchChannels();

    if (location.hash) {      
      setTimeout(() => {
        const el = document.querySelector(location.hash);        
        if (el) {          
          el.scrollIntoView({ behavior: 'smooth' }); // Smooth scrolling to the section
        }        
      }, 100); // Delay to ensure the DOM is fully loaded
    }
  }, [location, courseId, URL, fetchCourseDetail]);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;    
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });      
    } else if (type === 'file') {
      setFormData({ ...formData, slip: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      await axios.post(`${URL}/customers`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });      
      toast.success(localizedText.registerSuccess); // Show success message
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: '',
        company: '',
        address: '',
        taxId: '',
        slip: null,
        channelId: '',
        courseId: courseId,
        isCompany: 0,
        needInvoice: 0,
      });
      
      // Clear the file input field
      document.querySelector('input[name="slip"]').value = '';
      // Refresh course detail immediately after successful enrollment
      await fetchCourseDetail();
    } catch (error) {          
      const data = error.response?.data;
      let msg = "Registration failed. Please try again.";

      if (data) {
        if (Array.isArray(data.errors)) {          
          msg = data.errors.map(e => e.msg).join(", ");
        } else if (data.message) {
          msg = data.message;
        }
      }

      toast.error(msg);
    }
  };

  if (loading) {
    return <p className="text-center">{localizedText.loading}</p>;
  }

  if (!course) {
    return <p className="text-center">{localizedText.notFound}</p>;
  }

  // Format date to YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };  


  return (
    <div className="bg-gray-100 min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 px-4 py-2 bg-blue-900 text-white rounded-lg"
        >
          {localizedText.back}
        </button>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <img
            src={URL + `/images/course/${course.imageFile}`}
            alt={course.courseTopicTh}
            className="w-full h-80 object-cover rounded-md mb-4"
          />
          <h1 className="text-3xl font-bold mb-4">
            {course.courseTopicTh} ({course.courseTopicEn})
          </h1>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.objective}:</strong> {course.objective}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.courseOutline}:</strong>             
          </p>
          <ul className="list-disc pl-5">
            {chapters.map((chapter, index) => (
              <li key={chapter.chapterId} className="text-gray-700 mb-1">
                {localizedText.chapter} {index + 1}: {chapter.chapterTitle}
              </li>
            ))}
          </ul>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.targetAudience}:</strong> {audiences.map((audience) => audience.targetAudience).join(', ')}
          </p>         
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.learningFormat}:</strong>{' '}
            {(() => {
              if (course.learningFormat === 1) {
                return language === 'th' ? 'ออนไซต์' : 'Onsite';
              } else if (course.learningFormat === 2) {
                return language === 'th' ? 'ออนไลน์' : 'Online';
              } else if (course.learningFormat === 3) {
                return language === 'th' ? 'ออนไซต์และออนไลน์' : 'Both onsite and online';
              } else {
                return language === 'th' ? 'ไม่ระบุ' : 'Not specified';
              }
            })()}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.price}:</strong> {Number(course.price).toLocaleString()} {localizedText.priceUnit} 
          </p>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.date}:</strong> {formatDate(course.traningDate)}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.time}:</strong> {course.startTime} - {course.endTime}
          </p>
          <p className="text-gray-700 mb-2">
            <strong>{localizedText.place}:</strong> {course.place}
          </p>
        </div>

        {course.customerCount < course.maxSeat && (
        <div id="registers" className="scroll-mt-32">
        <form onSubmit={handleEnroll} className="bg-white p-6 rounded-lg shadow-md mt-6">
          <h2 className="text-2xl font-bold mb-4">{localizedText.enroll}</h2>
          <div className="col-span-2">

          <label className="block font-bold mb-2">{localizedText.registrantType}</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isCompany"
                  value="0"
                  checked={!formData.isCompany} // Checked when `isCompany` is false
                  onChange={(e) => setFormData({ ...formData, isCompany: 0, company: "",})}
                  className="mr-2"
                />
                {localizedText.personal}
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isCompany"
                  value="1"
                  checked={formData.isCompany} // Checked when `isCompany` is true
                  onChange={(e) => setFormData({ ...formData, isCompany: 1 })}
                  className="mr-2"
                />
                {localizedText.company}
              </label>
            </div>
          </div>
       
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="firstName"
              maxLength={100}              
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder={language === 'th' ? "ชื่อจริง" : "First Name"}
              className="p-2 border rounded"
              required
            />
            <input
              type="text"
              name="lastName"
              maxLength={100}
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder={language === 'th' ? "นามสกุล" : "Last Name"}
              className="p-2 border rounded"
              required
            />
            <input
              type="email"
              name="email"
              maxLength={100}
              value={formData.email}
              onChange={handleInputChange}
              placeholder={language === 'th' ? "อีเมล" : "Email"}
              className="p-2 border rounded"
              required
            />
            <input
              type="text"
              name="mobilePhone"              
              maxLength={10}              
              pattern="^[0-9]{10}$"
              title="รูปแบบเบอร์โทรไม่ถูกต้อง เช่น 0868473819"   
              value={formData.mobilePhone}           
              onChange={handleInputChange}
              placeholder={language === 'th' ? "เบอร์โทรศัพท์ (เช่น 0868473819)" : "Mobile Phone (e.g., 0868473819)"}
              className="p-2 border rounded"
              required
            />

            {Number(formData.isCompany)===1 && (
            <>
              <input
                type="text"
                name="company"
                maxLength={200}
                value={formData.company}
                onChange={handleInputChange}
                placeholder={language === 'th' ? "ชื่อบริษัท" : "Company Name"}
                className="p-2 border rounded"
                required
              />

            </>
            )}   

            <div className="col-span-2">
              <label className="block font-bold mb-2">{localizedText.needInvoice}</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="needInvoice"
                    value="0"
                    checked={!formData.needInvoice}
                    onChange={(e) => setFormData({ ...formData, needInvoice: 0, address: "", taxId: "" })}
                    className="mr-2"
                  />
                  {localizedText.no}
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="needInvoice"
                    value="1"
                    checked={formData.needInvoice}
                    onChange={(e) => setFormData({ ...formData, needInvoice: 1 })}
                    className="mr-2"
                  />
                  {localizedText.yes}
                </label>
              </div>
            </div>

              {Number(formData.needInvoice)===1 && (
              <>
                <input
                  type="text"
                  name="address"
                  maxLength={400}
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={language === 'th' ? "ที่อยู่" : "Address"}
                  className="p-2 border rounded"
                  required
                />
                <input
                  type="text"
                  name="taxId"
                  maxLength={13}
                  value={formData.taxId}
                  onChange={handleInputChange}
                  placeholder={language === 'th' ? "เลขประจำตัวผู้เสียภาษี" : "Tax ID"}
                  className="p-2 border rounded"
                  required
                />
              </>
            )}

            <div className="col-span-2">
              <label className="block font-bold mb-2">{localizedText.perceptionChannel}</label>
              <div className="flex flex-wrap gap-4">
                {channels.map((channel) => (
                  <label key={channel.channelId} className="flex items-center">
                    <input
                      type="radio"
                      name="channelId"
                      checked={formData.channelId === String(channel.channelId)} 
                      value={channel.channelId}
                      onChange={handleInputChange}
                      className="mr-2"
                      required
                    />
                    {channel.channelName}
                  </label>
                ))}
              </div>
            </div>

            <div className="col-span-2">
              <label className="block font-bold mb-2">{localizedText.uploadSlip}</label>
              <input
                type="file"
                name="slip"
                onChange={handleInputChange}
                className="p-2 border rounded"
                accept="image/*"
                required
              />
            </div>
          </div>
          <button
            type="submit"
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg"
          >
            {localizedText.enroll}
          </button>
        </form>
        </div>
        )}
        
      </main>
      <Footer />
    </div>
  );
}

export default CourseDetail;